import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BrandLogo } from "@/components/site/Chrome";
import { ItemsManager } from "@/components/admin/ItemsManager";
import { EnquiriesPanel, SettingsPanel, NotifyPanel } from "@/components/admin/Panels";
import { AlertsPanel } from "@/components/admin/AlertsPanel";
import { BookingsPanel } from "@/components/admin/BookingsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const inputCls = "mt-2 w-full bg-transparent border border-white/60 p-3 text-base normal-case tracking-normal outline-none focus:border-white transition-colors duration-200";

export function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await login(email, password); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen bg-gw-ground text-white flex items-center page-in" data-testid="admin-login-page">
      <div className="max-w-md w-full mx-auto px-6">
        <Link to="/" className="flex items-center gap-3 mb-12"><BrandLogo /></Link>
        <p className="lab text-gw-mist">Staff only</p>
        <h1 className="lux gw-h2 mt-4 mb-10">Manage listings.</h1>
        <form onSubmit={submit} className="grid gap-4">
          <label className="lab">Email<input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email-input" /></label>
          <label className="lab">Password<input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password-input" /></label>
          {error && <p className="text-sm bg-white/10 border border-white/30 p-3" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} className="lab bg-white text-gw-ground px-6 py-4 inline-flex justify-center items-center gap-2 hover:bg-gw-mist transition-colors duration-200 disabled:opacity-60" data-testid="login-submit-button">
            {busy && <Loader2 size={14} className="animate-spin" />}Sign in
          </button>
        </form>
      </div>
    </section>
  );
}

const TABS = [
  { v: "properties", label: "Wolfe Realtors · Properties" },
  { v: "projects", label: "Development · Projects" },
  { v: "shortlets", label: "Shortlets" },
  { v: "bookings", label: "Shortlet bookings" },
  { v: "enquiries", label: "Enquiries" },
  { v: "alerts", label: "Listing alerts" },
  { v: "settings", label: "Logo & contact" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="min-h-screen bg-gw-wash" />;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-gw-wash text-gw-deep" data-testid="admin-dashboard">
      <header className="bg-gw-ground text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-site-link"><BrandLogo small /><span className="lab text-gw-mist">Admin</span></Link>
          <div className="flex items-center gap-4 lab">
            <span className="hidden sm:inline text-gw-mist normal-case tracking-normal text-sm">{user.email}</span>
            <button type="button" onClick={logout} className="border border-white/50 px-4 py-2 hover:bg-white/10 transition-colors duration-200" data-testid="admin-logout-button">Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-12">
        <p className="lab text-gw-ground">Dashboard</p>
        <h1 className="lux gw-h2 mt-3 mb-10">Keep the site current.</h1>
        <Tabs defaultValue="properties">
          <TabsList className="rounded-none bg-transparent p-0 h-auto flex flex-wrap justify-start gap-2 mb-8">
            {TABS.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`}
                className="lab rounded-none border border-gw-ground/30 px-4 py-2 data-[state=active]:bg-gw-ground data-[state=active]:text-white data-[state=active]:shadow-none">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="bg-white p-6 md:p-8 border border-gw-ground/15">
            <TabsContent value="properties" className="mt-0"><ItemsManager kind="properties" /></TabsContent>
            <TabsContent value="projects" className="mt-0"><ItemsManager kind="projects" /></TabsContent>
            <TabsContent value="shortlets" className="mt-0"><ItemsManager kind="shortlets" /></TabsContent>
            <TabsContent value="bookings" className="mt-0"><BookingsPanel /></TabsContent>
            <TabsContent value="enquiries" className="mt-0"><NotifyPanel /><EnquiriesPanel /></TabsContent>
            <TabsContent value="alerts" className="mt-0"><AlertsPanel /></TabsContent>
            <TabsContent value="settings" className="mt-0"><SettingsPanel /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
